'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import {
  Calendar, Search, Plus, Edit2, Trash2, AlertCircle,
  Phone, Mail, Video, FileText, Clock, X, Download, Filter, MapPin, Activity, ChevronRight, ChevronLeft,
  ShieldCheck, ArrowUpRight, Crosshair, Zap, Info, Globe, Smartphone, MoreVertical,
  List, Columns, CalendarDays, CalendarRange, CheckSquare
} from 'lucide-react';
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../../utils/dateInput';
import { formatDate, formatDateTime, dayKey as companyDayKey } from '../../utils/formatDate';
import RowActionsMenu from '../../components/crm/RowActionsMenu';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
const eventTypes = {
  call:    { icon: Phone,    label: 'Voice Call',   color: 'text-[#0176D3] bg-[#E0F0FD]' },
  email:   { icon: Mail,     label: 'Email Sync',    color: 'text-[#0176D3] bg-[#EEF4FF]' },
  meeting: { icon: Calendar, label: 'Meeting',      color: 'text-[#2E844A] bg-[#EBF3ED]' },
  video:   { icon: Video,    label: 'Video Conf',   color: 'text-[#0176D3] bg-[#E0F0FD]' },
  note:    { icon: FileText, label: 'Session Note', color: 'text-[#FF9900] bg-[#FFF4E5]' },
  task:    { icon: CheckSquare, label: 'Task',      color: 'text-[#B85C00] bg-[#FDF0E4]' },
};

const HOUR_HEIGHT = 48; // px per hour in week view
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function formatHour(h) {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

// --- Month view: draggable event pill + droppable day cell ---
function MonthDraggableEvent({ activity, cfg, onEdit }) {
  // Tasks are shown on the calendar (by due date) but aren't draggable from here —
  // rescheduling a task updates a different record shape via a different endpoint
  // than a calendar activity, so drag-to-reschedule for tasks is intentionally out
  // of scope for now. useDraggable is still called unconditionally (required by
  // the rules of hooks); its listeners just never get attached to the DOM for a task.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `monthevent-${activity._id}` });
  const Icon = cfg.icon;
  const dragProps = activity.__isTask ? {} : { ...listeners, ...attributes };
  return (
    <button
      ref={setNodeRef}
      {...dragProps}
      onClick={(e) => { e.stopPropagation(); if (!isDragging && !activity.__isTask) onEdit(activity); }}
      title={activity.__isTask ? `Task: ${activity.title}` : activity.title}
      className={cn(
        "w-full flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-bold truncate text-left transition-transform hover:scale-[1.02]",
        activity.__isTask ? "cursor-default" : "cursor-grab active:cursor-grabbing",
        cfg.color, isDragging && "opacity-40"
      )}
    >
      <Icon size={10} className="shrink-0" />
      <span className="truncate">{activity.title}</span>
    </button>
  );
}

function MonthDayCell({ day, dayEvents, inMonth, isToday, isLastCol, onAdd, onEdit }) {
  const dateKey = day.toDateString();
  const { setNodeRef, isOver } = useDroppable({ id: `monthday-${dateKey}` });
  return (
    <div
      ref={setNodeRef}
      onClick={() => onAdd(day)}
      className={cn(
        "min-h-[110px] border-b border-r border-[#D8DDE6] p-1.5 flex flex-col gap-1 cursor-pointer transition-colors hover:bg-[#F3F2F2]/60",
        !inMonth && "bg-[#FAFAFA]",
        isLastCol && "border-r-0",
        isOver && "bg-[#0176D3]/10 ring-1 ring-inset ring-[#0176D3]"
      )}
      title="Click to schedule an event on this day"
    >
      <div className="flex items-center justify-between">
        <span className={cn(
          "text-[11px] font-bold w-6 h-6 flex items-center justify-center rounded-full",
          isToday ? "bg-[#0176D3] text-white" : inMonth ? "text-[#080707]" : "text-[#D8DDE6]"
        )}>
          {day.getDate()}
        </span>
      </div>
      <div className="flex-1 space-y-1 overflow-y-auto max-h-[80px] pr-0.5">
        {dayEvents.map(activity => (
          <MonthDraggableEvent key={activity._id} activity={activity} cfg={eventTypes[activity.type] || eventTypes.note} onEdit={onEdit} />
        ))}
      </div>
    </div>
  );
}

// --- Week view: hour grid with draggable + resizable event blocks ---
function WeekEventBlock({ activity, top, height, cfg, onEdit, onResizeStart }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `weekevent-${activity._id}` });
  const isTask = activity.__isTask;
  const style = {
    top,
    height: Math.max(height, 18),
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: isDragging ? 50 : 10,
  };
  const Icon = cfg.icon;
  const dragProps = isTask ? {} : { ...listeners, ...attributes };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold overflow-hidden shadow-sm select-none",
        isTask ? "cursor-default" : "cursor-grab active:cursor-grabbing",
        cfg.color, isDragging && "opacity-70 shadow-[0_2px_8px_rgba(0,0,0,0.16)]"
      )}
      {...dragProps}
      onClick={(e) => { e.stopPropagation(); if (!isDragging && !isTask) onEdit(activity); }}
      title={isTask ? `Task: ${activity.title}` : activity.title}
    >
      <div className="flex items-center gap-1 truncate">
        <Icon size={9} className="shrink-0" />
        <span className="truncate">{activity.title}</span>
      </div>
      {!isTask && (
        <div
          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-black/10"
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeStart(e, activity); }}
        />
      )}
    </div>
  );
}

function WeekDayColumn({ date, dayEvents, onSlotClick, onEdit, onResizeStart }) {
  const dateKey = date.toDateString();
  const { setNodeRef, isOver } = useDroppable({ id: `weekday-${dateKey}` });
  return (
    <div ref={setNodeRef} className={cn("relative flex-1 border-l border-[#D8DDE6]", isOver && "bg-[#0176D3]/5")}>
      {HOURS.map(h => (
        <div
          key={h}
          onClick={() => onSlotClick(new Date(date.getFullYear(), date.getMonth(), date.getDate(), h))}
          className="border-b border-[#EEF1F6] hover:bg-[#F3F2F2]/40 cursor-pointer"
          style={{ height: HOUR_HEIGHT }}
        />
      ))}
      {dayEvents.map(activity => {
        const dt = new Date(activity.scheduledAt || activity.createdAt);
        const top = (dt.getHours() + dt.getMinutes() / 60) * HOUR_HEIGHT;
        const height = ((activity.duration || 30) / 60) * HOUR_HEIGHT;
        return (
          <WeekEventBlock
            key={activity._id}
            activity={activity}
            top={top}
            height={height}
            cfg={eventTypes[activity.type] || eventTypes.note}
            onEdit={onEdit}
            onResizeStart={onResizeStart}
          />
        );
      })}
    </div>
  );
}

function WeekView({ weekStart, eventsByDate, onSlotClick, onEdit, onReschedule, onResize }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) =>
    new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)
  ), [weekStart]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_HEIGHT;
  }, []);

  const handleDragEnd = ({ active, delta, over }) => {
    if (!over) return;
    const activityId = String(active.id).replace('weekevent-', '');
    const activity = Object.values(eventsByDate).flat().find(a => a._id === activityId);
    if (!activity) return;
    const dayKey = String(over.id).replace('weekday-', '');
    const newDay = days.find(d => d.toDateString() === dayKey);
    if (!newDay) return;

    const original = new Date(activity.scheduledAt || activity.createdAt);
    const deltaMinutes = Math.round((delta.y / HOUR_HEIGHT) * 60 / 15) * 15;
    let totalMinutes = original.getHours() * 60 + original.getMinutes() + deltaMinutes;
    totalMinutes = Math.min(Math.max(totalMinutes, 0), 23 * 60 + 45);
    const newDate = new Date(newDay.getFullYear(), newDay.getMonth(), newDay.getDate(), 0, totalMinutes, 0);
    if (newDate.getTime() === original.getTime()) return;
    onReschedule(activity, newDate);
  };

  const onResizeStart = (e, activity) => {
    const startY = e.clientY;
    const startDuration = activity.duration || 30;

    const handleMove = () => {};
    const handleUp = (ev) => {
      const deltaY = ev.clientY - startY;
      const deltaMinutes = Math.round((deltaY / HOUR_HEIGHT) * 60 / 15) * 15;
      const newDuration = Math.max(15, startDuration + deltaMinutes);
      if (newDuration !== startDuration) onResize(activity, newDuration);
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="sf-card overflow-hidden">
        <div className="flex border-b border-[#D8DDE6] bg-[#F4F6F9]">
          <div className="w-12 shrink-0" />
          {days.map(d => {
            // `d` is a synthetic local-midnight grid-cell Date (not a real server
            // timestamp) — compare it with plain toDateString() to stay self-consistent
            // with how the rest of the grid is generated, rather than mixing in company
            // timezone here (which could shift a locally-built date across a day boundary).
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <div key={d.toISOString()} className="flex-1 text-center py-2.5 border-l border-[#D8DDE6]">
                <div className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                <div className={cn(
                  "text-[13px] font-bold mt-0.5 w-6 h-6 mx-auto flex items-center justify-center rounded-full",
                  isToday ? "bg-[#0176D3] text-white" : "text-[#080707]"
                )}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        <div ref={scrollRef} className="flex overflow-y-auto" style={{ maxHeight: 560 }}>
          <div className="w-12 shrink-0">
            {HOURS.map(h => (
              <div key={h} style={{ height: HOUR_HEIGHT }} className="relative">
                <span className="absolute -top-2 right-1.5 text-[9px] font-bold text-[#706E6B] bg-white pr-0.5">
                  {h === 0 ? '' : formatHour(h)}
                </span>
              </div>
            ))}
          </div>
          {days.map(d => (
            <WeekDayColumn
              key={d.toDateString()}
              date={d}
              dayEvents={eventsByDate[d.toDateString()] || []}
              onSlotClick={onSlotClick}
              onEdit={onEdit}
              onResizeStart={onResizeStart}
            />
          ))}
        </div>
      </div>
    </DndContext>
  );
}

export default function EventsPage() {
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('calendar');
  const [calendarMode, setCalendarMode] = useState('month'); // 'month' | 'week'
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const [formData, setFormData] = useState({
    title: '', type: 'call', scheduledAt: '', duration: 30, description: ''
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { fetchActivities(); fetchTasks(); }, []);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/crm/tasks', { params: { limit: 200 } });
      const taskList = response.data.tasks || [];
      const withDueDate = taskList.filter(t => t.dueDate && t.status !== 'completed' && t.status !== 'cancelled');
      setTasks(withDueDate.map(t => ({ ...t, scheduledAt: t.dueDate, type: 'task', duration: t.duration || 30, __isTask: true })));
    } catch (err) {
      // Tasks are supplementary to the calendar view — don't block activities on this failing.
    }
  };

  const fetchActivities = async (pageNum = 1) => {
    try {
      setLoading(pageNum === 1);
      setError(null);
      const response = await api.get('/crm/activities', { params: { page: pageNum, limit: 30 } });
      const actList = response.data.activities || response.data || [];
      const eventActivities = actList.filter(a => ['call', 'meeting', 'video', 'email', 'note'].includes(a.type));
      
      if (pageNum === 1) setActivities(eventActivities);
      else setActivities(prev => [...prev, ...eventActivities]);
      
      setTotalEvents(response.data.total || (pageNum * 30 + (eventActivities.length < 30 ? 0 : 1)));
      setPage(pageNum);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to sync schedule.';
      setError(msg);
      alertService.error('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    let result = activities;
    
    // Period Filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    
    if (filterPeriod === 'today') {
      // Both sides are real absolute timestamps, so compare "today" using the
      // company's configured timezone — keeps this consistent across teammates
      // in different browser timezones, instead of each person's local "today".
      const todayKey = companyDayKey(now);
      result = result.filter(a => companyDayKey(a.scheduledAt || a.createdAt) === todayKey);
    } else if (filterPeriod === 'week') {
      const weekAgo = today - (7 * 24 * 60 * 60 * 1000);
      result = result.filter(a => new Date(a.scheduledAt || a.createdAt).getTime() >= weekAgo);
    }

    if (!query) return result;
    return result.filter(a =>
      a.title?.toLowerCase().includes(query) ||
      a.description?.toLowerCase().includes(query)
    );
  }, [activities, searchQuery, filterPeriod]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const q = searchQuery.toLowerCase().trim();
    const matchesQuery = (a) => !q || a.title?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
    const source = viewMode === 'calendar'
      ? [...activities, ...tasks].filter(matchesQuery)
      : filtered;
    source.forEach(a => {
      const key = new Date(a.scheduledAt || a.createdAt).toDateString();
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [activities, tasks, filtered, viewMode, searchQuery]);

  const monthGrid = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstOfMonth.getDay());
    return Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  }, [currentMonth]);

  const goToPrevMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToToday = () => setCurrentMonth(new Date());

  const goToPrevWeek = () => setCurrentWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; });
  const goToNextWeek = () => setCurrentWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; });
  const goToTodayWeek = () => setCurrentWeekStart(getWeekStart(new Date()));

  const warnIfConflicts = (conflicts) => {
    if (!conflicts || conflicts.length === 0) return;
    const names = conflicts.map(c => `"${c.title}" at ${formatDateTime(c.scheduledAt)}`).join(', ');
    alertService.toast('warning', `Time conflict with ${conflicts.length} event(s): ${names}`);
  };

  const handleReschedule = async (activity, newDate) => {
    setActivities(prev => prev.map(a => a._id === activity._id ? { ...a, scheduledAt: newDate.toISOString() } : a));
    try {
      const response = await api.put(`/crm/activities/${activity._id}`, { scheduledAt: newDate.toISOString() });
      alertService.toast('success', 'Event rescheduled');
      warnIfConflicts(response.data.conflicts);
    } catch (err) {
      alertService.error('Error', 'Failed to reschedule event');
      fetchActivities();
    }
  };

  const handleResizeDuration = async (activity, newDuration) => {
    setActivities(prev => prev.map(a => a._id === activity._id ? { ...a, duration: newDuration } : a));
    try {
      await api.put(`/crm/activities/${activity._id}`, { duration: newDuration });
      alertService.toast('success', `Duration set to ${newDuration}m`);
    } catch (err) {
      alertService.error('Error', 'Failed to update duration');
      fetchActivities();
    }
  };

  const handleMonthDragEnd = ({ active, over }) => {
    if (!over) return;
    const activityId = String(active.id).replace('monthevent-', '');
    const dayKey = String(over.id).replace('monthday-', '');
    const activity = activities.find(a => a._id === activityId);
    const newDay = monthGrid.find(d => d.toDateString() === dayKey);
    if (!activity || !newDay) return;
    const original = new Date(activity.scheduledAt || activity.createdAt);
    if (newDay.toDateString() === original.toDateString()) return;
    const newDate = new Date(newDay.getFullYear(), newDay.getMonth(), newDay.getDate(), original.getHours(), original.getMinutes(), 0);
    handleReschedule(activity, newDate);
  };

  const handleAdd = (presetDate) => {
    setEditingId(null);
    setFormData({ title: '', type: 'call', scheduledAt: presetDate ? presetDate.toISOString() : '', duration: 30, description: '' });
    setShowAddModal(true);
  };

  const handleEdit = (activity) => {
    setEditingId(activity._id);
    setFormData(activity);
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      let response;
      if (editingId) {
        response = await api.put(`/crm/activities/${editingId}`, formData);
        alertService.success('Updated', 'Event saved');
      } else {
        response = await api.post('/crm/activities', formData);
        alertService.success('Success', 'Event created');
      }
      warnIfConflicts(response.data.conflicts);
      setShowAddModal(false);
      fetchActivities();
    } catch (err) {
      alertService.error('Error', 'Action failed');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (activity) => {
    const confirmed = await alertService.confirm('Delete Event?', `Permanently erase "${activity.title}"?`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await api.delete(`/crm/activities/${activity._id}`);
        alertService.success('Deleted', 'Event archived');
        fetchActivities();
      } catch (err) { alertService.error('Error', 'Action failed'); }
    }
  };

  if (loading && activities.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="Schedule Management"
        subtitle={<>{activities.length} Recorded interactions in pipeline</>}
        icon={Calendar}
        actions={<>
<button onClick={() => handleAdd()} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> Schedule Event
          </button>
  </>}
      />

      {/* KPI Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Sessions</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Voice Calls</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.filter(a => a.type === 'call').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#DD7A01]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Conferences</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.filter(a => a.type === 'video' || a.type === 'meeting').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Insights Logged</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.filter(a => a.type === 'note').length}</h3>
        </div>
      </div>

      {/* Control Bar */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-3 flex flex-wrap items-center gap-2 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[300px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
          <input 
            type="text" 
            placeholder="Search events by title or intel…" 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {viewMode === 'calendar' ? (
            <>
              {/* Month / Week toggle */}
              <div className="bg-white border border-[#D8DDE6] rounded p-0.5 flex items-center shadow-sm shrink-0">
                <button
                  onClick={() => setCalendarMode('month')}
                  title="Month view"
                  aria-label="Month view"
                  className={cn("px-2.5 h-7 rounded text-[10px] font-bold uppercase tracking-wider transition-all", calendarMode === 'month' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
                >
                  Month
                </button>
                <button
                  onClick={() => setCalendarMode('week')}
                  title="Week view"
                  aria-label="Week view"
                  className={cn("px-2.5 h-7 rounded text-[10px] font-bold uppercase tracking-wider transition-all", calendarMode === 'week' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
                >
                  Week
                </button>
              </div>

              {calendarMode === 'month' ? (
                /* Month Navigation */
                <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded shrink-0">
                  <button onClick={goToPrevMonth} title="Previous month" aria-label="Previous month" className="h-7 w-7 flex items-center justify-center rounded hover:bg-white text-[#706E6B] hover:text-[#0176D3] transition-all">
                    <ChevronLeft size={14} />
                  </button>
                  <button onClick={goToToday} className="px-3 h-7 rounded text-[11px] font-bold text-[#080707] hover:bg-white transition-all whitespace-nowrap">
                    {currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                  </button>
                  <button onClick={goToNextMonth} title="Next month" aria-label="Next month" className="h-7 w-7 flex items-center justify-center rounded hover:bg-white text-[#706E6B] hover:text-[#0176D3] transition-all">
                    <ChevronRight size={14} />
                  </button>
                  <button onClick={goToToday} className="ml-1 px-3 h-7 rounded text-[10px] font-bold text-[#0176D3] border border-[#C9C9C9] bg-white hover:bg-[#0176D3]/5 transition-all">
                    Today
                  </button>
                </div>
              ) : (
                /* Week Navigation */
                <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded shrink-0">
                  <button onClick={goToPrevWeek} title="Previous week" aria-label="Previous week" className="h-7 w-7 flex items-center justify-center rounded hover:bg-white text-[#706E6B] hover:text-[#0176D3] transition-all">
                    <ChevronLeft size={14} />
                  </button>
                  <button onClick={goToTodayWeek} className="px-3 h-7 rounded text-[11px] font-bold text-[#080707] hover:bg-white transition-all whitespace-nowrap">
                    {currentWeekStart.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} – {new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + 6).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                  </button>
                  <button onClick={goToNextWeek} title="Next week" aria-label="Next week" className="h-7 w-7 flex items-center justify-center rounded hover:bg-white text-[#706E6B] hover:text-[#0176D3] transition-all">
                    <ChevronRight size={14} />
                  </button>
                  <button onClick={goToTodayWeek} className="ml-1 px-3 h-7 rounded text-[10px] font-bold text-[#0176D3] border border-[#C9C9C9] bg-white hover:bg-[#0176D3]/5 transition-all">
                    Today
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Period Filter */
            <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded shrink-0">
              {[
                { value: 'all', label: 'All History' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'Weekly' }
              ].map(p => (
                <button
                  key={p.value}
                  className={cn(
                    "px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all",
                    filterPeriod === p.value ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
                  )}
                  onClick={() => setFilterPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          <div className="bg-white border border-[#D8DDE6] rounded p-0.5 flex items-center shadow-sm">
            <button
              onClick={() => setViewMode('calendar')}
              title="Calendar view"
              aria-label="Calendar view"
              className={cn("p-1.5 rounded transition-all", viewMode === 'calendar' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <CalendarDays size={14} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              title="List view"
              aria-label="List view"
              className={cn("p-1.5 rounded transition-all", viewMode === 'list' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <List size={14} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              title="Card view"
              aria-label="Card view"
              className={cn("p-1.5 rounded transition-all", viewMode === 'grid' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <Columns size={14} />
            </button>
          </div>

          <button
            onClick={() => { setSearchQuery(''); setFilterPeriod('all'); }}
            className="h-9 px-3 text-[11px] font-bold text-[#0176D3] hover:bg-[#0176D3]/5 rounded transition-all flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Main View Area */}
      {viewMode === 'calendar' ? (
        calendarMode === 'month' ? (
          <DndContext sensors={sensors} onDragEnd={handleMonthDragEnd}>
            <div className="sf-card overflow-hidden pb-2">
              {/* Weekday Header */}
              <div className="grid grid-cols-7 border-b border-[#D8DDE6] bg-[#F4F6F9]">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="px-2 py-2.5 text-center text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">{d}</div>
                ))}
              </div>

              {/* Month Grid */}
              <div className="grid grid-cols-7">
                {monthGrid.map((day, idx) => (
                  <MonthDayCell
                    key={idx}
                    day={day}
                    dayEvents={eventsByDate[day.toDateString()] || []}
                    inMonth={day.getMonth() === currentMonth.getMonth()}
                    isToday={day.toDateString() === new Date().toDateString()}
                    isLastCol={idx % 7 === 6}
                    onAdd={handleAdd}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
            </div>
          </DndContext>
        ) : (
          <WeekView
            weekStart={currentWeekStart}
            eventsByDate={eventsByDate}
            onSlotClick={handleAdd}
            onEdit={handleEdit}
            onReschedule={handleReschedule}
            onResize={handleResizeDuration}
          />
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-6 pb-12">
          {filtered.length > 0 ? filtered.map(activity => {
            const cfg = eventTypes[activity.type] || eventTypes.note;
            const Icon = cfg.icon;
            return (
              <div key={activity._id} className="sf-card p-6 flex flex-col justify-between hover:border-[#0176D3] transition-all group">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={cn("w-10 h-10 rounded flex items-center justify-center shadow-inner", cfg.color)}>
                      <Icon size={18} />
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEdit(activity)} title="Edit" aria-label="Edit event" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] rounded transition-colors"><Edit2 size={13} /></button>
                      <button onClick={() => handleDelete(activity)} title="Delete" aria-label="Delete event" className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  
                  <h3 className="text-[15px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors line-clamp-1">{activity.title}</h3>
                  <p className="text-[13px] text-[#706E6B] leading-relaxed line-clamp-2">{activity.description || 'No strategic intel markers recorded for this node.'}</p>
                </div>

                <div className="mt-6 pt-4 border-t border-[#D8DDE6] flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">
                      <Calendar size={12} className="text-[#0176D3]" /> 
                      {formatDate(activity.scheduledAt || activity.createdAt)}
                    </div>
                    {activity.duration && (
                      <div className="flex items-center gap-2 text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">
                        <Clock size={12} className="text-[#FF9900]" /> {activity.duration} Minutes
                      </div>
                    )}
                  </div>
                  <div className="sf-badge sf-badge-neutral">{activity.type}</div>
                </div>
              </div>
            );
          }) : (
            <div className="col-span-full py-32 flex flex-col items-center justify-center border-2 border-dashed border-[#D8DDE6] rounded opacity-50">
              <Calendar className="mx-auto mb-4 opacity-20" size={48} />
              <p className="text-sm font-bold text-[#706E6B] uppercase tracking-widest leading-loose">Schedule vacant</p>
            </div>
          )}
        </div>
      ) : (
        <div className="sf-table-container">
          <table className="sf-table">
            <thead>
              <tr>
                <th className="pl-6">Event Title</th>
                <th>Type</th>
                <th>Scheduled Date</th>
                <th>Duration</th>
                <th>Description</th>
                <th className="text-right pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map(activity => {
                const cfg = eventTypes[activity.type] || eventTypes.note;
                const Icon = cfg.icon;
                return (
                  <tr key={activity._id} className="group">
                    <td className="pl-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-8 h-8 rounded flex items-center justify-center", cfg.color)}>
                          <Icon size={14} />
                        </div>
                        <span className="font-bold text-[#0176D3] hover:underline cursor-pointer">{activity.title}</span>
                      </div>
                    </td>
                    <td><div className="sf-badge sf-badge-neutral">{activity.type.toUpperCase()}</div></td>
                    <td className="font-medium text-[#080707]">{formatDate(activity.scheduledAt || activity.createdAt)}</td>
                    <td className="font-medium text-[#706E6B]">{activity.duration ? `${activity.duration}m` : '—'}</td>
                    <td className="max-w-xs truncate text-[12px] text-[#706E6B]">{activity.description || '—'}</td>
                    <td className="text-right pr-6">
                      <RowActionsMenu actions={[
                        { label: 'Edit', icon: Edit2, onClick: () => handleEdit(activity) },
                        { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(activity) },
                      ]} />
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="py-24 text-center">
                    <Calendar className="mx-auto mb-4 opacity-10" size={48} />
                    <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">Zero records found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editingId ? 'Refine Objective' : 'Schedule Engagement'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Event Title</label>
                <input required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Interaction Type</label>
                  <select value={formData.type} onChange={e => setFormData(p => ({ ...p, type: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    {Object.entries(eventTypes).map(([k, v]) => <option key={k} value={k}>{v.label.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Duration (Min)</label>
                  <input type="number" value={formData.duration} onChange={e => setFormData(p => ({ ...p, duration: parseInt(e.target.value) }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Scheduled Target</label>
                <input type="datetime-local" value={toDatetimeLocalValue(formData.scheduledAt)} onChange={e => setFormData(p => ({ ...p, scheduledAt: fromDatetimeLocalValue(e.target.value) }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Notes</label>
                <textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} className="w-full min-h-[100px] p-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all resize-none focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
